import React, { useEffect, useState } from "react";
import { FaLocationDot } from "react-icons/fa6";
import { TbMailFilled } from "react-icons/tb";
import { Link, useNavigate } from "react-router-dom";
import Avtar from "../../../helper/images/admin.png";
import Smartphone from "../../../helper/svg/smartphone.svg";
import PRofileID from "../../../helper/svg/ID.svg";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserById } from "../../../api/admin/dashboard";
import { updateSettings, updatePassword } from "../../../api/common/settings";
import toast from "react-hot-toast";
import { useTranslation } from "../../../hooks/useTranslation";
import { useLanguage } from "../../../context/LanguageContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import SettingsCrudTab from "./SettingsCrudTab";
import {
  addCuisineType,
  addSpecialFeature,
  deleteCuisineType,
  deleteSpecialFeature,
  getCuisineTypes,
  getSpecialFeatures,
  updateCuisineType,
  updateSpecialFeature,
} from "../../../api/admin/cuisineFeatures";

const AdminSettings = () => {
  const { t } = useTranslation();
  const { setLanguage } = useLanguage();
  const passwordValidation = t("passwordValidation");
  const [enabled, setEnabled] = useState(true);
  const queryClient = useQueryClient();
  const [previewImage, setPreviewImage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    phoneNumber: "",
    language: "",
    timeFormat: "",
    timeZone: "",
    firstDayOfWeek: "",
    profilePicture: null,
    notifications: true,
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [hasShownEditHint, setHasShownEditHint] = useState(false);
  const navigate = useNavigate();
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [activeBottomTab, setActiveBottomTab] = useState("cuisine");


  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["user"],
    queryFn: getUserById,
    keepPreviousData: true,
  });

  const user = data?.data;

  const updateMutation = useMutation({
    mutationFn: (payload) => updateSettings(payload),
    onSuccess: () => {
      const successMessage =
        t("userprofile")?.toastMessages?.updateSuccess ||
        "Settings updated successfully";
      toast.success(successMessage);
      if (formData.language) {
        setLanguage(formData.language);
      }
      queryClient.invalidateQueries(["user"]);
      queryClient.invalidateQueries(["userLanguage"]);
    },
    onError: () => toast.error("Failed to update settings"),
  });


  const updatePasswordMutation = useMutation({
    mutationFn: (payload) => updatePassword(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["user"]);

      toast.success("Password updated successfully!");
      setFormData((prev) => ({
        ...prev,
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Something went wrong";
      toast.error(errorMessage);
    },
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        address: user.address || "",
        phoneNumber: user.phoneNumber || "",
        language: user.language || "",
        timeFormat: user.timeFormat || "",
        timeZone: user.timeZone || "",
        firstDayOfWeek: user.firstDayOfWeek || "",
        profilePicture: user.profilePicture || "",
        notifications: user.notifications ?? true,
      });
      setEnabled(user.notifications ?? true);
    }
  }, [user]);

  const activateEditing = () => {
    if (isEditing) return;
    setIsEditing(true);
    if (!hasShownEditHint) {
      toast(
        t("userprofile").editHint ||
          "Edit mode enabled. Save changes from the left panel."
      );
      setHasShownEditHint(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        profilePicture: file,
      }));
      setPreviewImage(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleUpdate = () => {
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "profilePicture" && value instanceof File) {
        data.append(key, value);
      } else {
        data.append(key, value);
      }
    });
    updateMutation.mutate(data);
    setIsEditing(false);
  };


  const handlePasswordChange = async () => {
    const { oldPassword, newPassword, confirmPassword } = formData;

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error(
        passwordValidation?.allFieldsRequired ||
          "All password fields are required."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(
        passwordValidation?.mismatch || "New passwords do not match."
      );
      return;
    }

    try {
      updatePasswordMutation.mutate({
        oldPassword,
        newPassword,
        confirmPassword,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error updating password.");
    }
  };


  return (
    <>
      <div className="flex gap-[10px] items-center">
        <div className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
          <IoIosArrowRoundBack
            size={24}
            onClick={() => navigate(-1)}
            className="text-[#fff]"
          />
        </div>
        <h2 className="text-[22px] font-medium">{t("adminsettings").title}</h2>
      </div>
      <div className="grid xl:grid-cols-[auto_1fr] grid-cols-1 gap-7 md:mb-[50px] mb-[30px] md:mt-[39px] mt-5">
        {/* ===========user details==================== */}
        <div className="w-full xl:max-w-[360px] bg-white p-2 box-shadow-detail">
          <div className="bg-[#FFFAF2] h-[132px] rounded-[3.56px]"></div>

          <label
            htmlFor="profilePictureInput"
            className="cursor-pointer w-full flex justify-center"
          >
            <img
              src={previewImage || `${user?.profilePicture}` || Avtar}
              alt="Profile"
              className="w-full max-w-[159px] h-[159px] mx-auto rounded-full border-4 border-white -mt-[80px]"
              style={{ pointerEvents: isEditing ? "auto" : "none" }}
            />
            {isEditing && (
              <input
                id="profilePictureInput"
                type="file"
                name="profilePicture"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleChange}
              />
            )}
          </label>
          <h2 className="text-xl font-medium text-[#353535] text-center mt-5">
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="border border-[#ccc] rounded px-2 py-1 w-full text-center"
              />
            ) : (
              user?.name
            )}
          </h2>
          <div className="bg-[#E1EDDE] px-2 py-1 text-center text-[#1B7200] rounded-[7.11px] w-max mx-auto mt-[7px] mb-5">
            {user?.role}
          </div>
          <div className="py-5 px-7 border-t border-[#E0E2E7]">
            <ul>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <img src={PRofileID} alt="" className="" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").userId}
                  </h5>
                  <p className="text-[#353535] text-base">{user?._id}</p>
                </div>
              </li>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <TbMailFilled className="text-[#667085] text-base" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").email}
                  </h5>
                  <p className="text-[#353535] text-base">{user?.email}</p>
                </div>
              </li>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <FaLocationDot className="text-[#667085] text-base" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").address}
                  </h5>
                  <p className="text-[#353535] text-base">
                    {isEditing ? (
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="border border-[#ccc] rounded px-2 py-1 w-full"
                      />
                    ) : (
                      user?.address || "N/A"
                    )}
                  </p>
                </div>
              </li>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <img src={Smartphone} alt="" className="" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").phoneNumber}
                  </h5>
                  <p className="text-[#353535] text-base ">
                    {isEditing ? (
                      <input
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        className="border border-[#ccc] rounded px-2 py-1 w-full"
                      />
                    ) : (
                      user?.phoneNumber
                    )}
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="px-[16px] mt-4 flex flex-col gap-3">
            {!isEditing ? (
              <button
                onClick={activateEditing}
                className="w-full text-center bg-[#EAF7E9] text-[#1B7200] font-semibold py-2 rounded-[10px] border border-[#B7E2B5] shadow-sm hover:bg-[#DFF1DE] transition-colors"
              >
                {t("userprofile").editProfile}
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 mb-[24px]">
                <button
                  onClick={handleUpdate}
                  className="flex-1 min-w-[120px] bg-[#4CAF50] text-white font-semibold py-2 rounded-[8px] shadow-sm hover:bg-[#43A047] transition-colors"
                >
                  {t("userprofile").save}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 min-w-[120px] border border-[#FF9D9D] text-[#FF0000] font-semibold py-2 rounded-[8px] hover:bg-[#FFF2F2] transition-colors"
                >
                  {t("userprofile").cancel}
                </button>
              </div>
            )}
          </div>
        </div>
        {/* ============user settings section=============== */}
        <div className="box-shadow-detail md:p-[25px] flex flex-col justify-between md:pb-[47px] py-[20px] px-[15px] bg-white">
          <div>
            {!isEditing && (
              <div className="flex items-center justify-between bg-[#F6FFF4] border border-[#CBEFD1] rounded-[10px] px-4 py-3 mb-5 text-sm text-[#1B7200]">
                <span>
                  {t("userprofile").lockedMessage ||
                    "Preferences are currently locked."}
                </span>
                <button
                  onClick={activateEditing}
                  className="text-[#4CAF50] font-semibold"
                >
                  {t("userprofile").editProfile}
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").timeZone}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").selectTimeZone}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="timeZone"
                value={formData.timeZone}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").selectTimeZone}
                </option>
                <option value="UTC">UTC</option>
                <option value="Asia/Calcutta">Asia/Calcutta</option>
                <option value="Asia/Kolkata">Asia/Kolkata</option>
                <option value="America/New_York">America/New_York</option>
                <option value="Europe/London">Europe/London</option>
                <option value="Europe/Paris">Europe/Paris</option>
                <option value="Asia/Tokyo">Asia/Tokyo</option>
                <option value="Australia/Sydney">Australia/Sydney</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").timeFormat}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").selectTimeFormat}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="timeFormat"
                value={formData.timeFormat}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").selectTimeFormat}
                </option>
                <option value="24">24-hr format (military clock)</option>
                <option value="12">12-hr format (AM/PM)</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").firstDayOfWeek}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").whenWeekStart}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="firstDayOfWeek"
                value={formData.firstDayOfWeek}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").Weekday}
                </option>

                <option value="monday">
                  {t("userprofile").options.monday}
                </option>
                <option value="tuesday">
                  {t("userprofile").options.tuesday}
                </option>
                <option value="wednesday">
                  {t("userprofile").options.wednesday}
                </option>
                <option value="thursday">
                  {t("userprofile").options.thursday}
                </option>
                <option value="friday">
                  {t("userprofile").options.friday}
                </option>
                <option value="saturday">
                  {t("userprofile").options.saturday}
                </option>
                <option value="sunday">
                  {t("userprofile").options.sunday}
                </option>
              </select>
            </div>
            <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] md:gap-7 gap-3   md:gap-[55px] items-center max-w-[668px] mb-[25px]">
              <div className="w-full max-w-[553px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").notification}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").notificationDesc}
                </p>
              </div>

              <div
                onClick={() => {
                  if (!isEditing) {
                    activateEditing();
                  }
                  setEnabled((prev) => !prev);
                  setFormData((prev) => ({
                    ...prev,
                    notifications: !prev.notifications,
                  }));
                }}
                className={`w-[60px] h-[31px] flex items-center rounded-full p-1 cursor-pointer transition-colors ${formData.notifications ? "bg-[#4CAF50]" : "bg-[#e1e1e1]"
                  }`}
              >
                <div
                  className={`bg-white w-[25px] h-[25px] rounded-full shadow-md transform transition-transform ${formData.notifications ? "translate-x-7" : "translate-x-0"
                    }`}
                />
              </div>
            </div>
            <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").language}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").selectLanguage}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="language"
                value={formData.language}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").selectLanguage}
                </option>
                <option value="English">English</option>
                <option value="Russian">Russian</option>
                <option value="Georgian">Georgian</option>
              </select>
            </div>
          </div>

          <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
          <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3 mb-[25px]">
            <div className="w-full max-w-[300px] inline-block">
              <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                {t("userprofile").changePassword || "Change Password"}
              </h3>
              <p className="text-[15px] text-[#18181B]">
                {t("userprofile").updatePassword || "Update your account password"}
              </p>
            </div>

            <div className="flex flex-col gap-3 max-w-[400px]">
              {[
                {
                  name: "oldPassword",
                  placeholder: t("userprofile")?.enterOldPassword || "Enter Old Password",
                },
                {
                  name: "newPassword",
                  placeholder: t("userprofile")?.enterNewPassword || "Enter New Password",
                },
                {
                  name: "confirmPassword",
                  placeholder: t("userprofile")?.confirmNewPassword || "Confirm New Password",
                },
              ].map((field) => (
                <div key={field.name} className="relative">
                  <input
                    type={showPasswords[field.name] ? "text" : "password"}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    className="w-full border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px]"
                  />

                  {/* 👁 Eye Icon (Font Awesome) */}
                  <span
                    onClick={() =>
                      setShowPasswords((prev) => ({
                        ...prev,
                        [field.name]: !prev[field.name],
                      }))
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-[#7E7E7E]"
                  >
                    {showPasswords[field.name] ? (
                      <FaEyeSlash size={18} />
                    ) : (
                      <FaEye size={18} />
                    )}
                  </span>
                </div>
              ))}

              <button
                onClick={handlePasswordChange}
                className="bg-[#4CAF50] text-white py-[10px] rounded-[5px] mt-2 hover:bg-[#43A047] transition-colors"
              >
                {t("userprofile").updatePasswordButton || "Update Password"}
              </button>
            </div>
          </div>

          {/* ============bottom settings tabs (Cuisine Type / Special Features)=============== */}
          <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setActiveBottomTab("cuisine")}
              className={`px-4 py-2 rounded-[10px] border text-sm font-semibold transition-colors ${
                activeBottomTab === "cuisine"
                  ? "bg-[#EAF7E9] text-[#1B7200] border-[#B7E2B5]"
                  : "bg-white text-[#353535] border-[#E0E0E0] hover:bg-[#F6FFF4]"
              }`}
            >
              {(t("newrestaurantadd")?.cuisineType || "Cuisine Type")
                .replace("*", "")
                .trim()}
            </button>
            <button
              type="button"
              onClick={() => setActiveBottomTab("features")}
              className={`px-4 py-2 rounded-[10px] border text-sm font-semibold transition-colors ${
                activeBottomTab === "features"
                  ? "bg-[#EAF7E9] text-[#1B7200] border-[#B7E2B5]"
                  : "bg-white text-[#353535] border-[#E0E0E0] hover:bg-[#F6FFF4]"
              }`}
            >
              {t("newrestaurantadd")?.specialFeatures || "Special Features"}
            </button>
          </div>

          {activeBottomTab === "cuisine" ? (
            <SettingsCrudTab
              title={(t("newrestaurantadd")?.cuisineType || "Cuisine Type")
                .replace("*", "")
                .trim()}
              addLabel={`Add ${
                (t("newrestaurantadd")?.cuisineType || "Cuisine Type")
                  .replace("*", "")
                  .trim()
              }`}
              queryKeyBase="admin-cuisine-types"
              fetchFn={getCuisineTypes}
              addFn={addCuisineType}
              updateFn={updateCuisineType}
              deleteFn={deleteCuisineType}
              getItemsFromResponse={(resp) =>
                resp?.data?.cuisines ||
                resp?.data?.cuisineTypes ||
                resp?.data?.items ||
                resp?.data?.data ||
                []
              }
              getPaginationFromResponse={(resp) =>
                resp?.data?.pagination ||
                resp?.data?.meta ||
                {}
              }
            />
          ) : (
            <SettingsCrudTab
              title={t("newrestaurantadd")?.specialFeatures || "Special Features"}
              addLabel={`Add ${
                t("newrestaurantadd")?.specialFeatures || "Special Features"
              }`}
              queryKeyBase="admin-special-features"
              fetchFn={getSpecialFeatures}
              addFn={addSpecialFeature}
              updateFn={updateSpecialFeature}
              deleteFn={deleteSpecialFeature}
              getItemsFromResponse={(resp) =>
                resp?.data?.features ||
                resp?.data?.specialFeatures ||
                resp?.data?.items ||
                resp?.data?.data ||
                []
              }
              getPaginationFromResponse={(resp) =>
                resp?.data?.pagination ||
                resp?.data?.meta ||
                {}
              }
            />
          )}

          {/* <div>
            <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] md:gap-7 gap-3 justify-between  xl:gap-[55px] items-center  mb-[25px]">
              <div className="w-full max-w-[521px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").deleteAccount}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").deleteAccountDesc}
                </p>
              </div>

              <Link
                to="#"
                className="text-[#FF0000] text-[17px] inline-block font-medium"
              >
                {t("userprofile").deleteAccount}
              </Link>
            </div>
          </div> */}
        </div>





      </div>
    </>
  );
};

export default AdminSettings;
